import { File, Folder, ChevronRight, ChevronDown } from "lucide-react"
import { useState } from "react"
import { cn } from "@/lib/utils"

export interface FileNode {
  name: string
  type: "file" | "folder"
  children?: FileNode[]
  path?: string
}

interface FileTreeProps {
  data: FileNode[]
  level?: number
  basePath?: string
  onFileClick?: (filePath: string) => void
}

export function FileTree({ data, level = 0, basePath = "", onFileClick }: FileTreeProps) {
  return (
    <div className="space-y-1">
      {data.map((node, index) => (
        <FileTreeNode 
          key={`${node.name}-${index}`} 
          node={node} 
          level={level}
          basePath={basePath}
          onFileClick={onFileClick}
        />
      ))}
    </div>
  )
}

function FileTreeNode({ 
  node, 
  level, 
  basePath,
  onFileClick 
}: { 
  node: FileNode
  level: number
  basePath: string
  onFileClick?: (filePath: string) => void
}) {
  const [isOpen, setIsOpen] = useState(level < 2)
  const currentPath = basePath ? `${basePath}/${node.name}` : node.name

  if (node.type === "file") {
    const handleClick = () => {
      if (onFileClick) {
        onFileClick(node.path || currentPath)
      }
    }

    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-2 py-1 text-sm text-muted-foreground hover:text-foreground hover:bg-accent w-full text-left transition-colors cursor-pointer"
        style={{ paddingLeft: `${level * 20 + 4}px` }}
      >
        <File className="h-4 w-4 shrink-0" />
        <span className="font-mono">{node.name}</span>
      </button>
    )
  }

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex w-full items-center gap-2 py-1 text-sm text-left transition-colors hover:text-foreground",
          isOpen ? "text-foreground" : "text-muted-foreground"
        )}
        style={{ paddingLeft: `${level * 20 + 4}px` }}
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0" />
        )}
        <Folder className="h-4 w-4 shrink-0 text-primary" />
        <span className="font-mono font-medium">{node.name}</span>
      </button>
      {isOpen && node.children && (
        <FileTree 
          data={node.children} 
          level={level + 1}
          basePath={currentPath}
          onFileClick={onFileClick}
        />
      )}
    </div>
  )
}

